<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import IdentityDuplicateReviewService from '@/services/identityDuplicateReview/identityDuplicateReviewService.ts';
import { IdentityDuplicateReviewStatusEnum } from '@/enums/identityDuplicateReview_enum.ts';
import { formatFullDate, formatIdentityDuplicateReviewStatus } from '@/utils/formatters.ts';

import type { DataTableHeader } from 'vuetify/framework';
import type { IdentityDuplicateReview } from '@/types/services/identityDuplicateReview/identityDuplicateReview.type.ts';

const { t } = useI18n()
const router = useRouter()

const reviews = ref<IdentityDuplicateReview[]>([])
const loading = ref<boolean>(false)

const statusFilter = ref<IdentityDuplicateReviewStatusEnum | null>(IdentityDuplicateReviewStatusEnum.OPEN)
const userId = ref<string>('')
const limit = ref<number>(10)
const page = ref<number>(1)
const total = ref<number>(0)
const maxPages = ref<number>(1)

const statusItems = computed(() => [
  { title: t('views.identityDuplicateReviewList.allStatuses'), value: null },
  ...Object.values(IdentityDuplicateReviewStatusEnum).map(status => ({
    title: t(`views.identityDuplicateReviewList.status.${status}`),
    value: status
  }))
])

const headers = computed<DataTableHeader[]>(() => [
  { title: t('views.identityDuplicateReviewList.header.id'), key: 'id', sortable: false, align: 'start' },
  { title: t('views.identityDuplicateReviewList.header.status'), key: 'status', sortable: false, align: 'start' },
  { title: t('views.identityDuplicateReviewList.header.user'), key: 'user', sortable: false, align: 'start' },
  { title: t('views.identityDuplicateReviewList.header.email'), key: 'email', sortable: false, align: 'start' },
  { title: t('views.identityDuplicateReviewList.header.matches'), key: 'matches', sortable: false, align: 'start' },
  { title: t('views.identityDuplicateReviewList.header.createdAt'), key: 'createdAt', sortable: false, align: 'start' },
])

function parseId(value: string): number | null {
  const parsed = Number(value)
  return value.trim() !== '' && Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

async function fetchReviews(): Promise<void> {
  try {
    loading.value = true
    const response = await IdentityDuplicateReviewService.getPaginated(page.value, limit.value, {
      status: statusFilter.value,
      userId: parseId(userId.value)
    })
    reviews.value = response.data
    total.value = response.total
    maxPages.value = response.maxPages
  } finally {
    loading.value = false
  }
}

onMounted(fetchReviews)

watch([statusFilter, userId], () => {
  if (page.value !== 1) {
    page.value = 1
  } else {
    fetchReviews()
  }
})
watch([limit, page], fetchReviews)

async function onRowClick(_: MouseEvent, row: { item: IdentityDuplicateReview }): Promise<void> {
  await router.push(`/identity-duplicate-reviews/${row.item.id}`)
}
</script>

<template>
  <MainTitleComponent icon="PhUsersFour">
    {{ t('views.identityDuplicateReviewList.title') }}
  </MainTitleComponent>

  <v-row align="center">
    <v-col cols="12" sm="5">
      <v-select
        v-model="statusFilter"
        :items="statusItems"
        :label="t('views.identityDuplicateReviewList.filters.status')"
        density="compact"
        hide-details
      />
    </v-col>
    <v-col cols="12" sm="4">
      <v-text-field
        v-model="userId"
        :label="t('views.identityDuplicateReviewList.filters.userId')"
        density="compact"
        hide-details
        inputmode="numeric"
      />
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-data-table-server
        :headers="headers"
        :items="reviews"
        :items-length="total"
        :items-per-page="limit"
        :loading="loading"
        :no-data-text="t('common.noResult')"
        :page="page"
        hover
        @update:page="page = $event"
        @update:items-per-page="limit = $event"
        @click:row="onRowClick"
      >
        <template #loading>
          <v-skeleton-loader type="table-row@4" />
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip :color="formatIdentityDuplicateReviewStatus(item.status)" size="small">
            {{ t(`views.identityDuplicateReviewList.status.${item.status}`) }}
          </v-chip>
        </template>
        <template #[`item.user`]="{ item }">
          {{ `${item.user.firstName} ${item.user.lastName}`.trim() }}
        </template>
        <template #[`item.email`]="{ item }">
          {{ item.user.email }}
        </template>
        <template #[`item.matches`]="{ item }">
          {{ item.matches.length }}
        </template>
        <template #[`item.createdAt`]="{ item }">
          {{ formatFullDate(item.createdAt) }}
        </template>
        <template #bottom>
          <v-pagination
            v-model="page"
            :length="maxPages"
            :tile="true"
            density="compact"
            total-visible="6"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>
</template>
